/**
 * ProjectRow — single row in the project list.
 * canonical: S2 lines 1385–1478 (.prj-row structure × 10 rows)
 *
 * Derives initials, avatar colour, status chip colour, and
 * outstanding-amount highlight purely from the Project record.
 * No Tailwind — all styles from tokens.css (.prj-row, .prj-ic,
 * .prj-name, .prj-addr, .prj-owner-block, .prj-money-block,
 * .prj-money-out, .prj-state, .chip).
 */

import type { Project } from '../../api/types'
import { formatCurrency } from '../../lib/formatters'
import {
  formatOutstandingForProject,
  projectHasBilledPayApps,
  billingProgressForProject,
} from '../../lib/projectMoneyDisplay'

// ── helpers ────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#1e293b', '#b91c1c', '#0f766e', '#1d4ed8',
  '#0284c7', '#a16207', '#0369a1', '#166534',
]

function initials(name: string | null): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('')
}

function avatarColor(name: string | null): string {
  if (!name) return AVATAR_COLORS[0]
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

interface StatusChip {
  label: string
  cls:   string   // chip colour class: c-red | c-amb | c-blue | c-grn | c-gry
}

function deriveStatus(p: Project): StatusChip {
  const outstanding = parseFloat(p.outstanding ?? '0')
  if (p.completed_at) {
    const m = String(p.completed_at).match(/^(\d{4})-(\d{2})-(\d{2})/)
    const label = m
      ? `Completed ${new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : 'Completed'
    return { label, cls: 'c-gry' }
  }
  if (p.status === 'completed') return { label: 'Completed', cls: 'c-gry' }
  if (outstanding === 0 && !projectHasBilledPayApps(p) && p.pay_app_count === 0) {
    return { label: 'Awaiting PA', cls: 'c-blue' }
  }
  if (outstanding > 0) return { label: 'Balance due', cls: 'c-amb' }
  return { label: 'Active', cls: 'c-grn' }
}

// ── component ──────────────────────────────────────────────────────────────

interface Props {
  project:     Project
  draftTotal?: number
  onClick:     (p: Project) => void
}

export default function ProjectRow({ project, draftTotal = 0, onClick }: Props) {
  const { label, cls } = deriveStatus(project)
  const outstanding     = parseFloat(project.outstanding ?? '0')
  const isLate          = outstanding > 0
  const showDraftOnly   = outstanding === 0 && draftTotal > 0
  const progress        = billingProgressForProject(project)

  return (
    <div className="prj-row" onClick={() => onClick(project)}>
      {/* canonical: .prj-ic — coloured initials avatar */}
      <div
        className="prj-ic"
        style={{ background: avatarColor(project.name) }}
      >
        {initials(project.name)}
      </div>

      {/* name + address */}
      <div>
        <div className="prj-name">
          {project.name ?? '—'}
          {project.is_test_project ? (
            <span className="chip" style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', background: 'var(--bg)', color: 'var(--ink4)' }}>TEST</span>
          ) : null}
        </div>
        <div className="prj-addr">{project.contact ?? project.owner ?? '—'}</div>
      </div>

      {/* owner block */}
      <div className="prj-owner-block">
        <div className="prj-owner">{project.owner ?? '—'}</div>
        <div className="prj-owner-s">Owner</div>
      </div>

      {/* contract block */}
      <div className="prj-money-block">
        <div className="prj-money">{formatCurrency(project.original_contract)}</div>
        <div className="prj-money-s">contract</div>
      </div>

      {/* outstanding block */}
      <div className="prj-money-block">
        <div className={`prj-money-out${isLate ? ' late' : ''}`} style={showDraftOnly ? { color: 'var(--ink4)' } : undefined}>
          {formatOutstandingForProject(project, draftTotal)}
        </div>
        <div className="prj-money-s">
          {outstanding > 0 ? 'outstanding' : showDraftOnly ? 'draft' : 'current'}
        </div>
      </div>

      {/* left to bill block */}
      <div className="prj-money-block" data-testid="prj-left-to-bill">
        <div className="prj-money">
          {progress.contractTotal > 0 ? formatCurrency(progress.remaining) : '—'}
        </div>
        <div className="prj-money-s">
          {progress.contractTotal > 0 ? `left · ${progress.pctBilled}% billed` : 'left to bill'}
        </div>
      </div>

      {/* status chip */}
      <div className="prj-state">
        <span className={`chip ${cls}`}>{label}</span>
      </div>
    </div>
  )
}
